import { apiGet, apiPost, apiPatch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";

import type {
  Client,
  ClientActivite,
  ClientAdresse,
  ClientContact,
  ClientNote,
  DashboardAlerte,
  DashboardAlertesResponse,
  DashboardResume,
  Commande,
  CommandeCreatePayload,
  CommandeCreateResponse,
  CommandeDetail,
  CommandeReservationPayload,
  CommandeReservationResponse,
  CommandeOuverte,
  CommandeStatut,
  Product,
  ProductDetail,
  ProductIdentifierResolution,
  ProductPrice,
  StockMovement,
  Supplier,
  BesoinAchat,
  CommandeFournisseur,
  CommandeFournisseurCreatePayload,
  CommandeFournisseurCreateResponse,
  CommandeFournisseurDetail,
  ReceptionFournisseurPayload,
  ReceptionFournisseurResponse,
  CommandeEligiblePreparation,
  PreparationEnCours,
  PreparationDetail,
  ScanPreparationPayload,
  ScanPreparationResponse,
  CommandePreteExpedition,
  ExpeditionDetail,
  CreationExpeditionPayload,
  ConfirmationExpeditionPayload,
  CommandeFacturable,
  FactureDetail,
  CreationFacturePayload,
  ConfirmationFacturePayload,
  AssociationSagePayload,
  PreuveLivraison,
  AssociationPreuveLivraisonPayload,
  DecisionPreuveLivraisonPayload,
  DocumentResolutionResult,
} from "@/types/domain";

// Couche service au-dessus des routes FastAPI existantes.
// Phase 1 : lecture seule. Les opérations métier (réservation, libération,
// expédition, réception, facturation) seront appelées via les routes POST
// existantes lors des phases suivantes — jamais recalculées côté frontend.
export const systemService = {
  health: () => apiGet<{ status: "ok" }>(ENDPOINTS.health),
  ready: () => apiGet<{ status: "ready" }>(ENDPOINTS.ready),
};

export const dashboardService = {
  resume: () => apiGet<DashboardResume>(ENDPOINTS.dashboard.resume),
  alertes: () => apiGet<DashboardAlertesResponse>(ENDPOINTS.dashboard.alertes),
  commandesOuvertes: () =>
    apiGet<CommandeOuverte[]>(ENDPOINTS.dashboard.commandesOuvertes),
};

export const clientsService = {
  list: (params?: Record<string, unknown>) =>
    apiGet<Client[]>(ENDPOINTS.clients.list, params),
  create: (data: {
    code_client: string;
    nom_legal: string;
    nom_commercial: string;
    type_client: string;
    statut: string;
    courriel_general: string;
    telephone_general: string;
    langue: string;
    devise: string;
  }) =>
    apiPost<{ id: string; message: string }>(
      ENDPOINTS.clients.list,
      data
    ),

  update: (
    clientId: string,
    data: {
      nom_legal: string;
      nom_commercial: string;
      type_client: string;
      statut: string;
      courriel_general: string;
      telephone_general: string;
      langue: string;
      devise: string;
    }
  ) =>
    apiPatch<{ id: string; message: string }>(
      ENDPOINTS.clients.detail(clientId),
      data
    ),

  detail: (clientId: string) => apiGet<unknown>(ENDPOINTS.clients.detail(clientId)),
  contacts: (clientId: string) =>
    apiGet<ClientContact[]>(ENDPOINTS.clients.contacts(clientId)),
  createContact: (
    clientId: string,
    data: {
      client_id: string;
      prenom: string;
      nom: string;
      titre_poste: string;
      departement: string;
      courriel: string;
      telephone: string;
      poste_telephonique: string;
      cellulaire: string;
      contact_principal: boolean;
      contact_facturation: boolean;
      contact_achats: boolean;
      contact_expedition: boolean;
      actif: boolean;
      notes: string;
    }
  ) =>
    apiPost<{ id: string; message: string }>(
      ENDPOINTS.clients.contacts(clientId),
      data
    ),
  updateContact: (
    clientId: string,
    contactId: string,
    data: {
      prenom?: string;
      nom?: string;
      titre_poste?: string;
      departement?: string;
      courriel?: string;
      telephone?: string;
      poste_telephonique?: string;
      cellulaire?: string;
      contact_principal?: boolean;
      contact_facturation?: boolean;
      contact_achats?: boolean;
      contact_expedition?: boolean;
      actif?: boolean;
      notes?: string;
    }
  ) =>
    apiPatch<{ id: string; message: string }>(
      `${ENDPOINTS.clients.contacts(clientId)}/${contactId}`,
      data
    ),

  updateAdresse: (
    clientId: string,
    adresseId: string,
    data: {
      type_adresse?: string;
      nom_adresse?: string;
      adresse_ligne_1?: string;
      adresse_ligne_2?: string;
      ville?: string;
      province_etat?: string;
      code_postal?: string;
      pays?: string;
      principale?: boolean;
      active?: boolean;
      instructions_livraison?: string;
    }
  ) =>
    apiPatch<{ id: string; message: string }>(
      `${ENDPOINTS.clients.adresses(clientId)}/${adresseId}`,
      data
    ),

  adresses: (clientId: string) =>
    apiGet<ClientAdresse[]>(ENDPOINTS.clients.adresses(clientId)),
  createAdresse: (
    clientId: string,
    data: {
      type_adresse: string;
      nom_adresse: string;
      adresse_ligne_1: string;
      adresse_ligne_2: string;
      ville: string;
      province_etat: string;
      code_postal: string;
      pays: string;
      principale: boolean;
      active: boolean;
      instructions_livraison: string;
    }
  ) =>
    apiPost<{ id: string; message: string }>(
      ENDPOINTS.clients.adresses(clientId),
      data
    ),
  activites: (clientId: string) =>
    apiGet<ClientActivite[]>(ENDPOINTS.clients.activites(clientId)),
  createActivite: (
    clientId: string,
    data: {
      contact_id: string | null;
      type_activite: string;
      sujet: string;
      description: string;
      date_echeance: string | null;
      statut: string;
      responsable_id: string | null;
    }
  ) =>
    apiPost<{ id: string; message: string }>(
      ENDPOINTS.clients.activites(clientId),
      data
    ),

  notes: (clientId: string) =>
    apiGet<ClientNote[]>(ENDPOINTS.clients.notes(clientId)),

  createNote: (
    clientId: string,
    data: {
      titre: string;
      contenu: string;
      type_note: string;
      confidentielle: boolean;
    }
  ) =>
    apiPost<ClientNote>(
      ENDPOINTS.clients.notes(clientId),
      data
    ),
};

export const produitsService = {
  list: (params?: Record<string, unknown>) =>
    apiGet<Product[]>(ENDPOINTS.produits.list, params),
  detail: (produitId: string) =>
    apiGet<ProductDetail>(ENDPOINTS.produits.detail(produitId)),
  prix: (produitId: string, quantite: number) =>
    apiGet<ProductPrice>(
      ENDPOINTS.produits.prix(produitId),
      { quantite }
    ),
  mouvements: (produitId: string) =>
    apiGet<StockMovement[]>(ENDPOINTS.produits.mouvements(produitId)),
  resolveIdentifier: (code: string) =>
    apiGet<ProductIdentifierResolution>(
      ENDPOINTS.catalogue.resolutionProduit,
      { code }
    ),
};

export const cataloguePreviewService = {
  resolveDocument: (referenceDocument: string, clientId: string) =>
    apiPost<DocumentResolutionResult>(ENDPOINTS.catalogue.resolutionDocumentaire, {
      reference_document: referenceDocument,
      client_id: clientId,
    }),
};

export const commandesService = {
  list: (params?: { statut?: CommandeStatut; client_id?: string }) =>
    apiGet<Commande[]>(ENDPOINTS.commandes.list, params),
  detail: (commandeId: string) =>
    apiGet<CommandeDetail>(ENDPOINTS.commandes.detail(commandeId)),
  create: (data: CommandeCreatePayload) =>
    apiPost<CommandeCreateResponse>(ENDPOINTS.commandes.create, data),
  reserver: (commandeId: string, data: CommandeReservationPayload) =>
    apiPost<CommandeReservationResponse>(
      ENDPOINTS.commandes.reserver(commandeId),
      data,
    ),
};

export const preparationsService = {
  eligibles: () =>
    apiGet<CommandeEligiblePreparation[]>(ENDPOINTS.preparations.eligibles),
  enCours: () =>
    apiGet<PreparationEnCours[]>(ENDPOINTS.preparations.enCours),
  creer: (commandeId: string) =>
    apiPost<PreparationDetail>(ENDPOINTS.preparations.creer(commandeId), {}),
  detail: (preparationId: string) =>
    apiGet<PreparationDetail>(ENDPOINTS.preparations.detail(preparationId)),
  scanner: (preparationId: string, payload: ScanPreparationPayload) =>
    apiPost<ScanPreparationResponse>(
      ENDPOINTS.preparations.scans(preparationId), payload,
    ),
  finaliser: (preparationId: string) =>
    apiPost<PreparationDetail>(ENDPOINTS.preparations.finaliser(preparationId), {}),
};

export const expeditionsService = {
  commandesPretes: () =>
    apiGet<CommandePreteExpedition[]>(ENDPOINTS.expeditions.commandesPretes),
  creer: (commandeId: string, payload: CreationExpeditionPayload) =>
    apiPost<ExpeditionDetail>(ENDPOINTS.expeditions.creer(commandeId), payload),
  historique: (commandeId: string) =>
    apiGet<ExpeditionDetail[]>(ENDPOINTS.expeditions.historique(commandeId)),
  detail: (expeditionId: string) =>
    apiGet<ExpeditionDetail>(ENDPOINTS.expeditions.detail(expeditionId)),
  confirmer: (expeditionId: string, payload: ConfirmationExpeditionPayload) =>
    apiPost<ExpeditionDetail>(ENDPOINTS.expeditions.confirmer(expeditionId), payload),
};

export const livraisonsService = {
  list: (statut?: string) =>
    apiGet<PreuveLivraison[]>(ENDPOINTS.livraisons.preuves, statut ? { statut } : undefined),
  detail: (preuveId: string) =>
    apiGet<PreuveLivraison>(ENDPOINTS.livraisons.detail(preuveId)),
  associer: (preuveId: string, payload: AssociationPreuveLivraisonPayload) =>
    apiPost<PreuveLivraison>(ENDPOINTS.livraisons.associer(preuveId), payload),
  valider: (preuveId: string, payload: DecisionPreuveLivraisonPayload) =>
    apiPost<PreuveLivraison>(ENDPOINTS.livraisons.valider(preuveId), payload),
  refuser: (preuveId: string, payload: DecisionPreuveLivraisonPayload) =>
    apiPost<PreuveLivraison>(ENDPOINTS.livraisons.refuser(preuveId), payload),
};

export const achatsService = {
  besoins: (params?: Record<string, unknown>) =>
    apiGet<BesoinAchat[]>(ENDPOINTS.achats.besoins, params),
  besoinDetail: (besoinId: string) =>
    apiGet<BesoinAchat>(ENDPOINTS.achats.besoinDetail(besoinId)),
  fournisseurs: () => apiGet<Supplier[]>(ENDPOINTS.fournisseurs),
  commandesFournisseur: (params?: Record<string, unknown>) =>
    apiGet<CommandeFournisseur[]>(ENDPOINTS.achats.commandesFournisseur, params),
  commandeFournisseurDetail: (commandeId: string) =>
    apiGet<CommandeFournisseurDetail>(
      ENDPOINTS.achats.commandeFournisseurDetail(commandeId),
    ),
  creerCommandeFournisseur: (data: CommandeFournisseurCreatePayload) =>
    apiPost<CommandeFournisseurCreateResponse>(
      ENDPOINTS.achats.commandesFournisseur,
      data,
    ),
  recevoirLigneFournisseur: (
    commandeId: string,
    ligneId: string,
    data: ReceptionFournisseurPayload,
  ) => apiPost<ReceptionFournisseurResponse>(
    ENDPOINTS.achats.recevoirLigneFournisseur(commandeId, ligneId),
    data,
  ),
};

export const facturationService = {
  commandesFacturables: () =>
    apiGet<CommandeFacturable[]>(ENDPOINTS.facturation.commandesFacturables),
  creer: (commandeId: string, data: CreationFacturePayload) =>
    apiPost<FactureDetail>(ENDPOINTS.facturation.creer(commandeId), data),
  historique: (commandeId: string) =>
    apiGet<FactureDetail[]>(ENDPOINTS.facturation.historique(commandeId)),
  detail: (factureId: string) =>
    apiGet<FactureDetail>(ENDPOINTS.facturation.detail(factureId)),
  confirmer: (factureId: string, data: ConfirmationFacturePayload) =>
    apiPost<FactureDetail>(ENDPOINTS.facturation.confirmer(factureId), data),
  associerSage: (factureId: string, data: AssociationSagePayload) =>
    apiPost<FactureDetail>(ENDPOINTS.facturation.associerSage(factureId), data),
  aRegulariser: () => apiGet<unknown[]>(ENDPOINTS.facturation.aRegulariser),
};

export const exceptionsService = {
  list: (params?: Record<string, unknown>) =>
    apiGet<unknown[]>(ENDPOINTS.exceptions.list, params),
  detail: (exceptionId: string) =>
    apiGet<unknown>(ENDPOINTS.exceptions.detail(exceptionId)),
};
